// ─────────────────────────────────────────────
//  leaveTestMode — the way out, and it has to actually work.
//
//  ⚠️ CLEARING THE FLAG IS NOT LEAVING. The snapshot store must be destroyed
//  too. `sandbox/exit.js` exists but effectively nothing calls it, which is why
//  the sandbox is a one-way door today; inheriting that here would mean the
//  next entry silently re-serves a stale day, and a tester would be looking at
//  edits they made hours ago believing them to be real.
//
//  ⚠️ THE ORDER MATTERS. Flag first, then the store. If the store delete fails
//  we still boot normally on the next reload — a stale store nobody reads is
//  harmless, whereas a live flag pointing at a half-deleted store is a session
//  that cannot render. Fail toward the normal app.
//
//  ⚠️ THE VIEW-AS PREVIEW GOES WITH IT. Testing mode always previews somebody;
//  leaving the impersonation behind would drop the admin back onto the real
//  board still wearing a clinician's identity and permissions.
// ─────────────────────────────────────────────
import { clearTestMode, TEST_MODE_PERSIST_KEY } from "../../domain/testMode";
import { deleteStoredSnapshot } from "../../sandbox/localDb";

// The view-as key AuthContext persists. Imported as a constant rather than
// reaching into AuthContext, which cannot be called outside React.
const VIEW_AS_KEY = "atc.viewAs";

export const hardReload = (path = "/") => {
  window.location.assign(path);
};

/**
 * Destroy the snapshot store. IndexedDB deletion is asynchronous and can BLOCK
 * indefinitely when another tab still holds the database open, so this never
 * waits forever — a leave that hangs is worse than a leftover store, because
 * the tester is stuck in a past day with a dead button.
 */
export function destroySnapshotStore(timeoutMs = 1500) {
  // ⚠️ DELETE THE KEY, NOT A DATABASE. This used to call
  // `indexedDB.deleteDatabase(TEST_MODE_PERSIST_KEY)`, which was wrong in the
  // worst possible way: every local store shares ONE database (`atc-sandbox`)
  // and distinguishes itself by `persistKey` INSIDE it, so that call deleted a
  // database that had never existed — and IndexedDB reports a delete of a
  // nonexistent database as SUCCESS. So this function returned true, the
  // warning never fired, and a real facility's captured day stayed on disk
  // permanently, hydrating into the NEXT testing session before it re-captured.
  // Caught in audit, 2026-09-07.
  //
  // The timeout still guards the caller: a leave that hangs strands a tester in
  // a past day with a dead button, which is worse than a leftover snapshot.
  return Promise.race([
    deleteStoredSnapshot(TEST_MODE_PERSIST_KEY),
    new Promise((resolve) => setTimeout(() => resolve(false), timeoutMs)),
  ]);
}

export async function leaveTestMode({ reload = hardReload, landing = "/setup?tab=testing" } = {}) {
  // Flag first: whatever happens next, the following boot is a normal one.
  clearTestMode();
  // ⚠️ sessionStorage IS WHERE IT LIVES (AuthContext.js:43, :298). This used to
  // clear localStorage, which is a silent no-op that reads as correct: the
  // preview would survive leaving, dropping the admin back onto the REAL board
  // still wearing a clinician's identity and permissions. localStorage is
  // cleared too, purely to sweep any key an older build left behind.
  try { sessionStorage.removeItem(VIEW_AS_KEY); } catch { /* hostile storage */ }
  try { localStorage.removeItem(VIEW_AS_KEY); } catch { /* hostile storage */ }

  const destroyed = await destroySnapshotStore();
  if (!destroyed) {
    // Not fatal, and not silent: a store that outlived its flag is dead weight
    // rather than a hazard, but somebody should know it is there.
    console.warn(
      "[testing] the snapshot store could not be deleted (another tab may hold it). " +
      "The flag is cleared, so this browser will boot normally."
    );
  }
  reload(landing);
  return { destroyed };
}

export default leaveTestMode;
